import { cn } from "@/lib/utils";
import { Camera } from "lucide-react";
import { ReactNode } from "react";
import { formatDate } from "@/core/i18n";

export interface MediaListItemProps {
  /** Image element (caller controls sizing, overlays, etc.). */
  image: ReactNode;
  /** Bold first line — a string, a JSX fragment, or a Skeleton for pending rows. */
  title: ReactNode;
  /** Sits beside the title and never truncates — for a short status chip. */
  titleSlot?: ReactNode;
  /** Second line (optional). Use <MediaListItemDate /> for the default camera + timestamp. */
  subtitle?: ReactNode;
  /** Right-aligned content — action icons, chips, upload progress, etc. */
  rightSlot?: ReactNode;
  /** Emerald selection highlight. */
  selected?: boolean;
  /** When provided, row renders as a <button>. Otherwise a <div>. */
  onClick?: () => void;
  /**
   * Leave room at the left edge for a control the CALLER renders beside this
   * row (the annotate list's selection checkbox).
   *
   * A prop rather than a slot because this row is a <button> whenever it is
   * clickable, and a checkbox nested inside a button is invalid HTML whose
   * clicks the outer button swallows. The control therefore has to be a DOM
   * sibling, positioned over the space this reserves.
   */
  leadingInset?: boolean;
}

const baseClass =
  "flex w-full items-center gap-4 border-b border-black/10 px-4 py-2 text-left";
const leadingInsetClass = "pl-10";

/**
 * Shared row chrome for media lists (photos, videos, annotation rows).
 * Keeps border, padding, hover, and selection behaviour consistent across
 * the capture and annotate pages. Callers supply the image + title content.
 */
export const MediaListItem = ({
  image,
  title,
  titleSlot,
  subtitle,
  rightSlot,
  selected,
  onClick,
  leadingInset,
}: MediaListItemProps) => {
  const body = (
    <>
      {image}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-xs font-bold leading-4 text-foreground/90">
            {title}
          </span>
          {titleSlot}
        </span>
        {subtitle}
      </div>
      {rightSlot}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          baseClass,
          leadingInset && leadingInsetClass,
          "cursor-pointer transition-colors hover:bg-black/[0.04]",
          selected && "bg-emerald-500/15 hover:bg-emerald-500/15",
        )}
      >
        {body}
      </button>
    );
  }

  return (
    <div className={cn(baseClass, leadingInset && leadingInsetClass)}>{body}</div>
  );
};

/**
 * Camera icon + formatted timestamp. Use as the `subtitle` slot for
 * photo/video rows so both lists render dates identically.
 */
export const MediaListItemDate = ({ iso }: { iso: string }) => (
  <div className="flex items-center gap-1 text-xs leading-4 text-foreground/60">
    <Camera className="size-4 shrink-0" />
    <span className="truncate">
      {formatDate(iso)}
    </span>
  </div>
);
